/**
 * Swaps a product's Trustpilot review list for its empty state when it has no
 * reviews — the same arrangement the live store uses.
 *
 * sections/reviews.liquid renders two copies of the widget:
 *   [data-trustpilot-default]     the review list at a fixed height, with
 *                                 data-no-reviews="collapse"
 *   [data-trustpilot-no-results]  hidden; the same widget at auto height with
 *                                 data-no-reviews="show"
 *
 * Whether a product has reviews is only known inside Trustpilot's cross-origin
 * iframe. Collapse is how it tells us: it sets display:none on the iframe when
 * there is nothing to show. The moment that happens the default copy is hidden
 * and the empty-state copy revealed, so a product with no reviews doesn't reserve
 * the full list height.
 *
 * The live store checks once, 500ms after load, and only on desktop — which can
 * run before Trustpilot has finished. Watching for the collapse itself removes
 * that race and works at every width.
 *
 * Both copies start deferred (assets/trustpilot.js only adds Trustpilot's own
 * `trustpilot-widget` class once a widget nears the viewport), so the widgets
 * are found by the theme's `tp-widget` class and the empty-state copy is handed
 * to Trustpilot here — it was hidden when the viewport observer ran, so nothing
 * else will do it.
 */

/**
 * How long to wait after Trustpilot is handed the widget before treating a page
 * with no iframe as "no reviews". Long enough for a slow render, short enough
 * that the reserved height is not left standing.
 */
const RENDER_TIMEOUT = 6000;
const LOADED_ATTR = "data-tp-loaded";

if (!customElements.get("reviews-trustpilot-swap")) {
  customElements.define(
    "reviews-trustpilot-swap",
    class ReviewsTrustpilotSwap extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.defaultWrap = this.querySelector("[data-trustpilot-default]");
        this.fallbackWrap = this.querySelector("[data-trustpilot-no-results]");
        this.widget = this.defaultWrap?.querySelector(".tp-widget, .trustpilot-widget");
        if (!this.widget || !this.fallbackWrap) return;

        // Trustpilot injects the iframe, then toggles its inline style, so the
        // whole widget is observed rather than a single node.
        this.observer = new MutationObserver(() => this.check());
        this.observer.observe(this.widget, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["style"],
        });

        this.check();
        this.watchRender();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.renderWatcher?.disconnect();
        clearTimeout(this.timer);
      }

      /**
       * Collapse is only reported once Trustpilot has actually run. When it
       * renders nothing at all — an unknown SKU, a blocked request — there is no
       * iframe to collapse and the reserved height would sit there empty, so the
       * empty state is shown instead.
       */
      watchRender() {
        if (this.widget.hasAttribute(LOADED_ATTR)) {
          this.startTimeout();
          return;
        }

        this.renderWatcher = new MutationObserver(() => {
          if (!this.widget.hasAttribute(LOADED_ATTR)) return;
          this.renderWatcher.disconnect();
          this.startTimeout();
        });
        this.renderWatcher.observe(this.widget, {
          attributes: true,
          attributeFilter: [LOADED_ATTR],
        });
      }

      startTimeout() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          const frame = this.widget.querySelector("iframe");
          const showing =
            frame &&
            window.getComputedStyle(frame).display !== "none" &&
            frame.getBoundingClientRect().height > 0;
          if (!showing) this.swap();
        }, RENDER_TIMEOUT);
      }

      check() {
        const frame = this.widget.querySelector("iframe");
        if (!frame) return;

        // Collapsed is Trustpilot's way of saying "no reviews for these SKUs".
        const collapsed =
          frame.style.display === "none" || window.getComputedStyle(frame).display === "none";
        if (!collapsed) return;

        this.swap();
      }

      swap() {
        if (this.swapped) return;
        this.swapped = true;

        this.observer?.disconnect();
        this.renderWatcher?.disconnect();
        clearTimeout(this.timer);

        this.defaultWrap.classList.add("hidden");
        this.fallbackWrap.classList.remove("hidden");
        this.render(this.fallbackWrap.querySelector(".tp-widget, .trustpilot-widget"));
      }

      /**
       * Renders a widget that was still waiting its turn. Trustpilot's bootstrap
       * is already on the page by now — the default copy could not have reported
       * a collapse without it.
       */
      render(widget) {
        if (!widget || widget.hasAttribute(LOADED_ATTR)) return;

        widget.removeAttribute("data-tp-deferred");
        widget.classList.add("trustpilot-widget");
        widget.setAttribute(LOADED_ATTR, "");

        try {
          window.Trustpilot?.loadFromElement?.(widget, true);
        } catch (error) {
          console.warn("trustpilot: empty-state widget failed to render", error);
        }
      }
    },
  );
}
